import qs from 'qs'

export function querySerializer(query: Record<string, any> = {}) {
  try {
    return qs.stringify(query, {
      arrayFormat: 'brackets', // test[]=1&test[]=2
      encode: false, // 避免 %5B%5D；若後端要求可改 true
      skipNulls: true, // 避免 null 參數
    })
  } catch (error) {
    console.error('Error serializing query:', error)
    return ''
  }
}

/**
 * 解析價格字串或數字，移除千分位符號並轉換為數字
 * @param value - 價格值（可以是數字、字串、null 或 undefined）
 * @returns 解析後的數字，無效值返回 0
 */
export function parsePrice(value: number | string | null | undefined): number {
  if (value === null || value === undefined) return 0
  if (typeof value === 'string') {
    return Number(value.replace(/,/g, '')) || 0
  }
  return Number(value) || 0
}

/**
 * 判斷價格是否被降價（折扣價大於或等於原價時，視為無實質降價）
 * @param discount - FunNow 折扣價
 * @param rrp - 原價（Recommended Retail Price）
 * @returns 當兩個價格都有效且折扣價 < 原價時返回 true（有降價），否則返回 false
 */
export function isPriceReduced(discount: number | string | null | undefined, rrp: number | string | null | undefined): boolean {
  const discountPrice = parsePrice(discount)
  const originalPrice = parsePrice(rrp)
  // 當兩個價格都有效且折扣價小於原價時，才算有實質降價
  return discountPrice > 0 && originalPrice > 0 && discountPrice < originalPrice
}

type UserCoords = { lat: number; lng: number }

/**
 * Resolve the user's current GPS coordinates via the browser Geolocation API.
 * Returns `null` when the API is unavailable (e.g. SSR, insecure context) or
 * when the user denies the permission / lookup fails. Always resolves —
 * never rejects — so callers can use a single conditional check.
 */
export function getCurrentCoords(): Promise<UserCoords | null> {
  return new Promise(resolve => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      resolve(null)
      return
    }

    navigator.geolocation.getCurrentPosition(
      position => resolve({ lat: position.coords.latitude, lng: position.coords.longitude }),
      () => resolve(null)
    )
  })
}

export function scheduleIdle(cb: IdleRequestCallback, opts?: IdleRequestOptions): () => void {
  if (import.meta.server) {
    return () => {}
  }

  if (window.requestIdleCallback) {
    const handle = window.requestIdleCallback(cb, opts)

    return () => window.cancelIdleCallback(handle)
  }

  // Decaying budget mimics the spec's `timeRemaining` semantics so callers that
  // chunk work (`while (deadline.timeRemaining() > 0) ...`) eventually break out
  // instead of busy-looping on a constant value.
  const start = performance.now()
  const FRAME_BUDGET_MS = 50

  const handle = window.setTimeout(() => {
    const deadline: IdleDeadline = {
      didTimeout: false,
      timeRemaining: () => Math.max(0, FRAME_BUDGET_MS - (performance.now() - start)),
    }

    cb(deadline)
  }, opts?.timeout ?? 0)

  return () => window.clearTimeout(handle)
}
